<script setup>
import CardTitle from "@/Components/Links/CardTitle.vue";
import { withPadding } from "@/utils/styleUtils";
import { computed, onMounted, ref } from "vue";

const props = defineProps({
    link: { type: Object, required: true },
    appearance: { type: Object, default: () => ({}) },
    buttonStyle: { type: [Object, Array, String], default: () => ({}) },
    DoBucket: String,
    DoEndpoint: String,
    // Hide left shop icon by default for Shop buttons
    hideLeftImage: { type: Boolean, default: true },
});

const emit = defineEmits(["click", "share"]);

const currentEffect = computed(() => {
    try {
        // ShopButton stores effect directly as link.animation_effect
        const eff = props.link?.animation_effect;
        return eff ? String(eff) : "";
    } catch (e) {
        return "";
    }
});

const initialAnimateClass = ref("");
const onAnimEnd = () => {
    initialAnimateClass.value = "";
};

onMounted(() => {
    if (currentEffect.value)
        initialAnimateClass.value = `${currentEffect.value}-animate`;
});
</script>

<template>
    <button
        :id="link.id"
        type="button"
        @click="emit('click', link)"
        class="btn align-items-center justify-content-between position-relative w-100 link-button"
        :class="[
            appearance.buttons || 'btn-light',
            appearance.button_margin_top || 'mt-0',
            link.animation_effect || '',
            initialAnimateClass,
            'shop-general',
        ]"
        @animationend="onAnimEnd"
        :style="[
            buttonStyle,
            withPadding('15px'),
            {
                '--hover-bg': appearance.button_background_hover || '#eee',
                '--hover-text': appearance.button_hover_color || '#fff',
                '--hover-transform': appearance.transform || 'scale(1.02)',
            },
        ]"
    >
        <div
            class="d-flex align-items-center w-100 px-2 position-relative"
            :style="{ paddingRight: '36px' }"
        >
            <!-- Left Shop Icon -->
            <div v-if="!hideLeftImage" class="left-icon me-2">
                <div
                    class="classic-icon d-flex align-items-center justify-content-center"
                    :class="appearance.buttons"
                    aria-hidden="true"
                >
                    <!-- Store/Bag icon -->
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="icon"
                    >
                        <path
                            d="M3 7l1.8-3.6A2 2 0 0 1 6.6 2h10.8a2 2 0 0 1 1.8 1.4L21 7"
                        />
                        <path d="M3 7h18v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
                        <path d="M16 11a4 4 0 0 1-8 0" />
                    </svg>
                </div>
            </div>
            <div v-else class="left-spacer me-2"></div>

            <!-- Center Title -->
            <div class="center flex-grow-1 text-center mb-1">
                <slot name="title">
                    <CardTitle
                        :title="link.title || ''"
                        layout="classic"
                        align="center"
                        :max="100"
                    />
                </slot>
            </div>


        </div>

        <!-- Default slot for expandable content -->
        <div class="w-100">
            <slot></slot>
        </div>
    </button>
</template>

<style scoped>
/* Remove default button padding for Shop card so inner grid can go edge-to-edge */
.shop-general {
    padding: 0;
}

/* Fixed side widths to keep title centered */
.right {
    width: 28px;
    display: flex;
    justify-content: center;
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
}
.left-spacer {
    width: 28px;
    flex-shrink: 0;
}
.center {
    max-width: calc(100% - 80px);
}

/* Icon sizing to mirror GeneralButton */
.classic-icon {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 6px;
    flex-shrink: 0;
    background: #f1f5f9; /* subtle bg for consistency */
    color: #64748b;
}
</style>
